import { DropdownProps } from '@toggle/design-system';
import {
  AllEntitiesResolved,
  Entity,
  EntityResolved,
  EntitySearchType,
  formatTicker,
} from '@toggle/toggle';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TruncateTooltip } from '~/components/truncate-tooltip/TruncateTooltip';
import { useTagsSearch } from '~/hooks/use-tags-search/useTagsSearch';

import { EntityDropdownItem } from '../../entity-dropdown-item/EntityDropdownItem';
import * as S from './EntityCell.styles';

export interface EntityCellBaseHandlers {
  onChangeEntity: (
    prevEntity: EntityResolved,
    newEntity: EntityResolved
  ) => void;
  onAddEntity: (entity: EntityResolved) => void;
  onRemoveEntity: (entity: EntityResolved) => void;
}

const EntityCellBase = ({
  entity,
  handlers,
}: {
  entity: AllEntitiesResolved;
  handlers: EntityCellBaseHandlers;
}) => {
  const { t } = useTranslation(['watchlistManager', 'analyze']);
  const [shouldShowDropdownItems, setShouldShowDropdownItems] = useState(false);

  const { tags: tagList, searchTags } = useTagsSearch({
    searchType: EntitySearchType.Entity,
  });
  const { onChangeEntity, onRemoveEntity, onAddEntity } = handlers;

  const hasInput = !!entity.input;
  const hasEntityData = !!entity.tag;
  const parsedEntity = entity as Entity;
  const entityTicker = hasEntityData
    ? parsedEntity.ticker.toLowerCase()
    : entity.input;

  const filteredTagList = useMemo(
    () =>
      tagList.filter(
        t =>
          t.tag.split('_')[0].toLowerCase() === entityTicker ||
          t.ticker === entityTicker
      ),
    [tagList, entityTicker]
  );
  const hasAlternatives = !!filteredTagList.length;
  const isUnmappedWithEntityData = !hasEntityData && hasAlternatives;

  useEffect(() => {
    searchTags(entityTicker);
  }, [entityTicker]);

  const getContent = () => {
    if (!hasEntityData) {
      if (hasAlternatives) {
        return (
          <S.NoneOfTheAboveLabel>
            {t('watchlistManager:noneOfTheAbove')}
          </S.NoneOfTheAboveLabel>
        );
      }

      return (
        <S.DisabledText>{t('watchlistManager:notAvailable')}</S.DisabledText>
      );
    }

    const ticker = formatTicker(parsedEntity);
    return (
      <S.LogoAndTickerWrapper>
        <S.StyledAssetLogoContainer entity={parsedEntity} size={20} />
        <TruncateTooltip
          atLength={17}
          text={ticker}
          truncatedLabelWrapper={<S.Text>{ticker}</S.Text>}
        />
      </S.LogoAndTickerWrapper>
    );
  };

  const handleSelectItem = (newEntity: Entity) => {
    if (isUnmappedWithEntityData) {
      onAddEntity({ ...newEntity, input: entity.input });
    } else {
      onChangeEntity(
        { ...parsedEntity, input: entity.input },
        { ...newEntity, input: entity.input }
      );
    }
  };

  const onNoneOfTheAboveClick = () => {
    setShouldShowDropdownItems(false);
    onRemoveEntity({ ...parsedEntity, input: entity.input });
  };

  return (
    <S.Root>
      <S.ContentWrapper>{getContent()}</S.ContentWrapper>
      {hasAlternatives && (
        <S.StyledDropdown<FC<DropdownProps<Entity>>>
          className="entity-watchlist-dropdown"
          shouldShowItems={shouldShowDropdownItems}
          dropdownItems={filteredTagList}
          onItemsShowChange={setShouldShowDropdownItems}
          selectItem={handleSelectItem}
          activeItemIdx={
            filteredTagList.some(e => e.tag === entity.tag)
              ? filteredTagList.findIndex(e => e.tag === entity.tag)
              : undefined
          }
          renderDropdownItem={(item, isActive, isFocused) => (
            <EntityDropdownItem
              entity={item}
              isActive={isActive}
              isFocused={isFocused}
            />
          )}
          afterInListNode={
            !isUnmappedWithEntityData &&
            hasInput && (
              <S.NoneOfTheAboveWrapper
                onClick={onNoneOfTheAboveClick}
                data-testid="none-of-the-above-button"
              >
                <S.NoneOfTheAboveContentWrapper>
                  {t('watchlistManager:noneOfTheAbove')}
                </S.NoneOfTheAboveContentWrapper>
              </S.NoneOfTheAboveWrapper>
            )
          }
        >
          <S.IconWrapper>
            <S.StyledIcon iconName="ChevronLightDown" size={16} />
          </S.IconWrapper>
        </S.StyledDropdown>
      )}
    </S.Root>
  );
};

export const EntityCell = React.memo(EntityCellBase);
